import { AddOutlined } from '@mui/icons-material'
import { Avatar } from '@mui/material'
import {
    DataGrid,
    GridColDef,
    GridPaginationModel,
    GridSortModel,
} from '@mui/x-data-grid'
import { createFileRoute, stripSearchParams } from '@tanstack/react-router'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod/v4-mini'
import { ConfirmationDialog } from '@/components/ConfirmationDialog'
import { FabLink } from '@/components/FabLink'
import { AdminOverviewPagePendingComponent } from '@/features/admin/components/AdminOverviewPagePendingComponent'
import { CustomAdminDataGridToolbar } from '@/features/admin/components/CustomAdminDataGridToolbar'
import { assertUserIsAdminOrRedirect } from '@/features/admin/helper/assertUserIsAdminOrRedirect'
import { userFieldNameAndSortByEnumMap } from '@/features/admin/users/overview/helper/userFieldNameAndSortByEnumMap'
import { usersQueryFilters } from '@/features/admin/users/overview/helper/usersQueryFilters'
import { useDeleteUser } from '@/features/admin/users/overview/mutations/useDeleteUser'
import { DeletedItemSelection } from '@/generated/Enumerations/DeletedItemSelection_pb'
import { IdentityUserSortBy } from '@/generated/Enumerations/IdentityUserSortBy_pb'
import { IdentityUserDto } from '@/generated/Messages/Basic_pb'
import { UuidDto } from '@/generated/Messages/Common_pb'
import { debounce } from '@/helper/debounce'
import { generateAvatarColor } from '@/helper/generateAvatarColor'
import { stringifyId } from '@/helper/id'
import { useCreateCUDColumn } from '@/hooks/useCreateCUDColumn'
import { usersQueryOptions, useUsers } from '@/queries/useUsers'
import {
    searchParamsDeletedItemSelectionDefaultValues,
    searchParamsDeletedItemSelectionSchema,
} from '@/schemas/searchParams/searchParamsDeletedItemSelectionSchema'
import {
    availablePageSizes,
    searchParamsPaginationDefaultValues,
    searchParamsPaginationSchema,
} from '@/schemas/searchParams/searchParamsPaginationSchema'
import {
    searchParamsSearchTextDefaultValues,
    searchParamsSearchTextSchema,
} from '@/schemas/searchParams/searchParamsSearchTextSchema'
import {
    searchParamsSortingDefaultValues,
    searchParamsSortingSchema,
} from '@/schemas/searchParams/searchParamsSortingSchema'

const seleniumPrefix = 'users'

const useStaticColumns = (): GridColDef<IdentityUserDto>[] => {
    const { t } = useTranslation()

    return React.useMemo(
        () => [
            {
                field: 'initials',
                headerName: t('user.initials'),
                valueGetter: (_, row) =>
                    `${row.firstName?.[0] ?? ''} ${row.lastName?.[0] ?? ''}`
                        .trim()
                        .toUpperCase(),
                renderCell: ({ row, value }) => {
                    const bgColor = generateAvatarColor(
                        row.firstName,
                        row.lastName,
                        row.email
                    )

                    return row.firstName || row.lastName ?
                            <Avatar sx={{ margin: 1, bgcolor: bgColor }}>
                                {value}
                            </Avatar>
                        :   null
                },
                sortable: false,
                filterable: false,
                hideable: false,
                resizable: false,
            },
            {
                field: 'firstName',
                headerName: t('user.firstName'),
                flex: 1,
                minWidth: 150,
                filterable: false,
                hideable: false,
                resizable: false,
            },
            {
                field: 'lastName',
                headerName: t('user.lastName'),
                flex: 1,
                minWidth: 150,
                filterable: false,
                hideable: false,
                resizable: false,
            },
            {
                field: 'email',
                headerName: t('user.email'),
                flex: 1,
                minWidth: 150,
                filterable: false,
                hideable: false,
                resizable: false,
            },
            {
                field: 'roles',
                headerName: t('user.role'),
                flex: 1,
                minWidth: 150,
                filterable: false,
                hideable: false,
                resizable: false,
                valueGetter: (_, { roles: [role] }) => role?.name,
            },

            {
                field: 'deletedAt',
                headerName: t('common.active'),
                flex: 0.5,
                minWidth: 150,
                valueGetter: (_, row) => !row.timestamps?.deletedAt,
                sortable: false,
                hideable: false,
                resizable: false,

                type: 'boolean',
                filterable: true,
            },
        ],
        [t]
    )
}
const RouteComponent: React.FC = () => {
    const {
        deletedItemSelection,
        page,
        pageSize,
        sortByField,
        sortDirectionDesc,
        searchText,
    } = Route.useSearch()

    const navigate = Route.useNavigate()
    const { t } = useTranslation()

    const { data } = useUsers(
        usersQueryFilters(
            deletedItemSelection,
            pageSize,
            page,
            sortByField,
            sortDirectionDesc,
            searchText
        )
    )
    const staticColumns = useStaticColumns()
    const deleteUser = useDeleteUser()
    const [userToDelete, setUserToDelete] = React.useState<UuidDto>()
    const cudColumn = useCreateCUDColumn<IdentityUserDto>({
        viewLinkProps: row => ({
            to: '/admin/users/$id/view',
            params: { id: stringifyId(row.id) },
        }),
        editLinkProps: row => ({
            to: '/admin/users/$id/edit',
            params: { id: stringifyId(row.id) },
            disabled: !!row.timestamps?.deletedAt,
        }),
        deleteProps: row => ({
            onClick: () => setUserToDelete(row.id),
            disabled: !!row.timestamps?.deletedAt,
        }),
    })

    const columns = React.useMemo<GridColDef<IdentityUserDto>[]>(
        () => [...staticColumns, cudColumn],
        [staticColumns, cudColumn]
    )

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const onChangeSearchText = React.useCallback(
        debounce(async (searchText: string) => {
            await navigate({
                search: prev => ({ ...prev, searchText, page: 0 }),
            })
        }),
        []
    )

    const paginationModel = React.useMemo(
        (): GridPaginationModel => ({ page, pageSize }),
        [page, pageSize]
    )
    const sortModel = React.useMemo(
        (): GridSortModel =>
            (
                sortByField !== IdentityUserSortBy.UNSPECIFIED &&
                userFieldNameAndSortByEnumMap[sortByField]
            ) ?
                [
                    {
                        field: userFieldNameAndSortByEnumMap[sortByField],
                        sort: sortDirectionDesc ? 'desc' : 'asc',
                    },
                ]
            :   [],
        [sortByField, sortDirectionDesc]
    )
    const filterModel = React.useMemo(
        () =>
            deletedItemSelection !== DeletedItemSelection.BOTH ?
                {
                    items: [
                        {
                            field: 'deletedAt',
                            operator: 'is',
                            value:
                                deletedItemSelection ===
                                DeletedItemSelection.NORMAL_ONLY,
                        },
                    ],
                }
            :   undefined,
        [deletedItemSelection]
    )

    return (
        <div>
            <title>{t('user.urlUser')}</title>

            <DataGrid
                rows={data.users}
                columns={columns}
                pageSizeOptions={availablePageSizes}
                disableRowSelectionOnClick={true}
                getRowId={({ userName }) => userName}
                slots={{ toolbar: CustomAdminDataGridToolbar }}
                slotProps={{
                    toolbar: {
                        createButton: (
                            <FabLink
                                variant={'extended'}
                                color={'primary'}
                                disabled={false}
                                to={'/admin/users/create'}
                                sx={{
                                    display: { xs: 'none', sm: 'inline-flex' },
                                }}
                                data-selenium={`${seleniumPrefix}-new`}
                            >
                                <AddOutlined />
                                {t('common.new')}
                            </FabLink>
                        ),
                        initialSearchText: searchText,
                        onChangeSearchText,
                    },
                }}
                paginationMode={'server'}
                filterMode={'server'}
                sortingMode={'server'}
                rowCount={data.pagination?.totalItemCount ?? 0}
                paginationModel={paginationModel}
                sortModel={sortModel}
                filterModel={filterModel}
                onPaginationModelChange={async model =>
                    await navigate({ search: prev => ({ ...prev, ...model }) })
                }
                onFilterModelChange={async model => {
                    const deletedItemSelection = model.items.find(
                        i => i.field === 'deletedAt'
                    )?.value as boolean | undefined

                    await navigate({
                        search: prev => ({
                            ...prev,

                            page: 0,

                            deletedItemSelection:
                                deletedItemSelection === undefined ?
                                    DeletedItemSelection.BOTH
                                : deletedItemSelection ?
                                    DeletedItemSelection.NORMAL_ONLY
                                :   DeletedItemSelection.DELETED_ONLY,
                        }),
                    })
                }}
                onSortModelChange={async ([model]) => {
                    const castField = model?.field as
                        | keyof IdentityUserDto
                        | undefined
                    const field =
                        castField ?
                            userFieldNameAndSortByEnumMap[castField]
                        :   undefined

                    await navigate({
                        search: prev => ({
                            ...prev,

                            page: 0,

                            sortByField:
                                field ?? IdentityUserSortBy.UNSPECIFIED,
                            sortDirectionDesc: model?.sort === 'desc',
                        }),
                    })
                }}
                showToolbar={true}
            />
            <FabLink
                variant={'extended'}
                color={'primary'}
                disabled={false}
                to={'/admin/users/create'}
                sx={theme => ({
                    display: { xs: 'inline-flex', sm: 'none' },
                    position: 'absolute',
                    bottom: theme.spacing(2),
                    right: theme.spacing(2),
                })}
                data-selenium={`${seleniumPrefix}-new`}
            >
                <AddOutlined />
                {t('common.new')}
            </FabLink>

            <ConfirmationDialog
                open={!!userToDelete}
                onClickNo={() => setUserToDelete(undefined)}
                onClickYes={async () => {
                    if (!userToDelete) {
                        return
                    }

                    setUserToDelete(undefined)
                    await deleteUser.mutateAsync(userToDelete)
                }}
            />
        </div>
    )
}

const PendingComponent: React.FC = () => {
    const { page, pageSize, searchText } = Route.useSearch()
    const cudColumn = useCreateCUDColumn<IdentityUserDto>()
    const staticColumns = useStaticColumns()
    const { t } = useTranslation()

    const columns = React.useMemo(
        () => [...staticColumns, cudColumn],
        [staticColumns, cudColumn]
    )

    return (
        <AdminOverviewPagePendingComponent
            pageTitle={t('user.urlUser')}
            columns={columns}
            page={page}
            pageSize={pageSize}
            searchText={searchText}
        />
    )
}

export const Route = createFileRoute('/__base-layout/admin/users/')({
    component: RouteComponent,
    validateSearch: z.object({
        ...searchParamsDeletedItemSelectionSchema.shape,
        ...searchParamsPaginationSchema.shape,
        ...searchParamsSortingSchema(
            IdentityUserSortBy,
            IdentityUserSortBy.UNSPECIFIED
        ).shape,
        ...searchParamsSearchTextSchema.shape,
    }),
    search: {
        middlewares: [
            stripSearchParams({
                ...searchParamsDeletedItemSelectionDefaultValues,
                ...searchParamsPaginationDefaultValues,
                ...searchParamsSortingDefaultValues(
                    IdentityUserSortBy.UNSPECIFIED
                ),
                ...searchParamsSearchTextDefaultValues,
            }),
        ],
    },

    beforeLoad: async ({
        search: {
            deletedItemSelection,
            page,
            pageSize,
            sortByField,
            sortDirectionDesc,
            searchText,
        },
        context: { queryClient, grpcClients, user },
    }) => {
        assertUserIsAdminOrRedirect(user)

        await queryClient.ensureQueryData(
            usersQueryOptions(
                grpcClients,
                usersQueryFilters(
                    deletedItemSelection,
                    pageSize,
                    page,
                    sortByField,
                    sortDirectionDesc,
                    searchText
                )
            )
        )
    },
    pendingMs: 2000,
    pendingComponent: PendingComponent,
})
